import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Check, ScanFace } from 'lucide-react';
import { getCosmeticSkincareStatus } from './CosmeticSkinCareApi';

import processingIcon from '../../Assets/Images/rewamp/skin/2. Analysis loader/processing.svg';
import mappingIcon from '../../Assets/Images/rewamp/skin/2. Analysis loader/mapping.svg';
import personalizingIcon from '../../Assets/Images/rewamp/skin/2. Analysis loader/personalised recommendation.svg';
import preparingIcon from '../../Assets/Images/rewamp/skin/2. Analysis loader/preparing your final.svg';

const STEPPER = ['Face Scan', 'Details', 'Analysis', 'Results'];

const STAGES = [
  { key: 'processing', icon: processingIcon, label: 'Processing your inputs' },
  { key: 'mapping', icon: mappingIcon, label: 'Mapping your skin concerns' },
  { key: 'personalizing', icon: personalizingIcon, label: 'Personalizing recommendations' },
  { key: 'preparing', icon: preparingIcon, label: 'Preparing your final results' },
];

const getFrontImage = () => {
  try {
    const stored = JSON.parse(sessionStorage.getItem('cosmeticFaceImages') || '{}');
    return stored.front || null;
  } catch {
    return null;
  }
};

const POLL_INTERVAL_MS = 9000;
const MAX_AUTO_ATTEMPTS = 13; // ~2 minutes of auto-polling before handing control to the user

const CosmeticAnalysis = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(5);
  const [frontImage] = useState(getFrontImage);
  const [resultRecord, setResultRecord] = useState(null);
  const [pollError, setPollError] = useState('');
  const [timedOut, setTimedOut] = useState(false);
  const [checkingNow, setCheckingNow] = useState(false);
  const pollTimeoutRef = useRef(null);
  const attemptsRef = useRef(0);
  const cancelledRef = useRef(false);

  const complete = Boolean(resultRecord);
  const activeStageIndex = complete ? STAGES.length - 1 : Math.min(STAGES.length - 1, Math.floor(progress / 25));

  // Creep the visual progress bar upward while we wait on the real backend status.
  useEffect(() => {
    if (complete) {
      setProgress(100);
      return undefined;
    }
    const id = setInterval(() => {
      setProgress((p) => Math.min(92, p + 1));
    }, 400);
    return () => clearInterval(id);
  }, [complete]);

  const checkOnce = useCallback(async () => {
    const requestId = sessionStorage.getItem('cosmeticRequestId');
    const loginObj = JSON.parse(localStorage.getItem('loginObj') || 'null');
    const patientid = loginObj?.code;

    if (!requestId || !patientid) {
      setPollError('Missing request details — please restart from Face Scan.');
      return true; // nothing to poll for, stop
    }

    try {
      const res = await getCosmeticSkincareStatus(requestId, patientid);
      const record = res.data;
      if (cancelledRef.current) return true;
      // STATUS: 0 queued, 3 backend accepted/still polling — both still pending.
      // 1 done, 2 failed — both terminal, hand back to the UI either way.
      if (record && (record.STATUS === 1 || record.STATUS === 2)) {
        setResultRecord(record);
        try {
          sessionStorage.setItem('cosmeticResultRecord', JSON.stringify(record));
        } catch {}
        return true; // done
      }
      setPollError('');
      return false; // still pending
    } catch (err) {
      if (cancelledRef.current) return true;
      setPollError('Having trouble checking your results — retrying…');
      return false;
    }
  }, []);

  // Schedules the next poll tick. Skips (without counting against the attempt
  // budget) while the tab is backgrounded — no point polling nobody's watching.
  const scheduleNext = useCallback(() => {
    pollTimeoutRef.current = setTimeout(async () => {
      if (cancelledRef.current) return;
      if (document.hidden) {
        scheduleNext();
        return;
      }

      const done = await checkOnce();
      if (cancelledRef.current || done) return;

      attemptsRef.current += 1;
      if (attemptsRef.current >= MAX_AUTO_ATTEMPTS) {
        setTimedOut(true);
        return;
      }
      scheduleNext();
    }, POLL_INTERVAL_MS);
  }, [checkOnce]);

  useEffect(() => {
    cancelledRef.current = false;
    attemptsRef.current = 0;

    (async () => {
      const done = await checkOnce();
      if (cancelledRef.current || done) return;
      scheduleNext();
    })();

    return () => {
      cancelledRef.current = true;
      clearTimeout(pollTimeoutRef.current);
    };
  }, [checkOnce, scheduleNext]);

  const handleCheckAgain = async () => {
    if (checkingNow || complete) return;
    setCheckingNow(true);
    const done = await checkOnce();
    setCheckingNow(false);
    if (!done) {
      // Give the user another bounded auto-polling window instead of leaving them stuck.
      setTimedOut(false);
      attemptsRef.current = 0;
      cancelledRef.current = false;
      scheduleNext();
    }
  };

  const handleBack = () => navigate('/cosmetic-skincare-analysis/details');

  const handleViewResults = () => {
    navigate('/cosmetic-skincare-analysis/results');
  };

  // Once the report is ready, give the user a few seconds to click through
  // themselves; if they don't, move on automatically.
  useEffect(() => {
    if (!complete) return undefined;
    const id = setTimeout(() => {
      navigate('/cosmetic-skincare-analysis/results');
    }, 3000);
    return () => clearTimeout(id);
  }, [complete, navigate]);

  return (
    <div className="mx-auto w-full">
      <style>{`
        .ca-wrapper {
          background-color: #fff; background-image: var(--section-bg-gradient);
          padding: 24px 40px 64px 40px;
          min-height: 80vh;
        }

        .ca-header {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          margin-bottom: 40px;
        }

        .ca-back {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 600;
          font-size: 16px;
          color: #1A1A2E;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          justify-self: start;
        }

        .ca-stepper {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }

        .ca-stepper-caption {
          font-size: 12px;
          color: #676C80;
        }

        .ca-stepper-track {
          display: flex;
          align-items: center;
        }

        .ca-stepper-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          width: 90px;
        }

        .ca-stepper-circle {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 700;
          background: #ffffff;
          border: 1.5px solid #C9C4D9;
          color: #8B86A8;
        }

        .ca-stepper-item.active .ca-stepper-circle {
          background: var(--color-brand-primary);
          border-color: var(--color-brand-primary);
          color: #ffffff;
        }

        .ca-stepper-item.done .ca-stepper-circle {
          background: #ffffff;
          border-color: var(--color-brand-primary);
          color: var(--color-brand-primary);
        }

        .ca-stepper-label {
          font-size: 12px;
          font-weight: 600;
          color: #8B86A8;
        }

        .ca-stepper-item.active .ca-stepper-label,
        .ca-stepper-item.done .ca-stepper-label {
          color: var(--color-brand-primary);
        }

        .ca-stepper-line {
          width: 60px;
          height: 1.5px;
          background: #C9C4D9;
          margin-bottom: 20px;
        }

        .ca-stepper-line.done {
          background: var(--color-brand-primary);
        }

        .ca-center {
          max-width: 640px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .ca-face-wrapper {
          position: relative;
          width: 220px;
          height: 260px;
          border-radius: 50% / 45%;
          padding: 3px;
          background: linear-gradient(135deg, #22D1EE 0%, #004276 60%, #003360 100%);
          margin-bottom: 28px;
        }

        .ca-face-inner {
          position: relative;
          width: 100%;
          height: 100%;
          border-radius: 50% / 45%;
          overflow: hidden;
          background: #DFF1FB;
        }

        .ca-face-photo {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .ca-face-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #80DAF1;
        }

        .ca-scan-line {
          position: absolute;
          left: 8%;
          right: 8%;
          height: 2px;
          background: #22D1EE;
          box-shadow: 0 0 8px 2px rgba(34, 209, 238, 0.8);
          animation: caScanMove 2.2s ease-in-out infinite;
        }

        @keyframes caScanMove {
          0% { top: 20%; }
          50% { top: 78%; }
          100% { top: 20%; }
        }

        .ca-checkmark-svg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
        }

        .ca-heading {
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 24px;
          line-height: 30px;
          color: var(--color-brand-primary);
          margin-bottom: 8px;
        }

        .ca-subtext {
          font-size: 13px;
          color: var(--color-brand-primary);
          max-width: 480px;
          margin-bottom: 28px;
        }

        .ca-stage-row {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: center;
          margin-bottom: 28px;
        }

        .ca-stage-chip {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #ffffff;
          border: 1px solid #DFF1FB;
          border-radius: 999px;
          padding: 8px 14px;
          font-size: 12px;
          font-weight: 600;
          color: #676C80;
        }

        .ca-stage-chip.active {
          border-color: var(--color-brand-primary);
          background: #EAF3FA;
          color: var(--color-brand-primary);
        }

        .ca-stage-chip.done {
          color: var(--color-brand-primary);
        }

        .ca-stage-icon {
          width: 20px;
          height: 20px;
        }

        .ca-stage-check {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: var(--color-brand-primary);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ca-progress-row {
          width: 100%;
          max-width: 460px;
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .ca-progress-label {
          color: var(--color-text-subheading);
        }

        .ca-progress-pct {
          color: var(--color-brand-primary);
        }

        .ca-progress-track {
          width: 100%;
          max-width: 460px;
          height: 6px;
          border-radius: 999px;
          background: #EAF3FA;
          position: relative;
          margin-bottom: 10px;
        }

        .ca-progress-fill {
          height: 100%;
          border-radius: 999px;
          background: var(--color-brand-primary);
        }

        .ca-progress-handle {
          position: absolute;
          top: 50%;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--color-brand-primary);
          border: 2px solid #ffffff;
          box-shadow: 0px 1px 4px 0px #00000040;
          transform: translate(-50%, -50%);
        }

        .ca-caption {
          font-size: 13px;
          color: #676C80;
          margin-bottom: 24px;
        }

        .ca-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          font-size: 14px;
          color: #ffffff;
          background: var(--color-brand-primary);
          border: none;
          border-radius: 999px;
          padding: 12px 28px;
          cursor: pointer;
        }

        .ca-btn-primary.pulsing {
          animation: ca-btn-pulse 0.6s ease-in-out infinite;
        }

        @keyframes ca-btn-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }

        @media (max-width: 768px) {
          .ca-wrapper { padding: 16px 56px 40px 16px; }
          .ca-header { grid-template-columns: 1fr; gap: 12px; }
          .ca-stepper-track { flex-wrap: nowrap; overflow-x: auto; justify-content: flex-start; -webkit-overflow-scrolling: touch; }
          .ca-stepper-item { width: 62px; flex-shrink: 0; }
          .ca-stepper-line { width: 20px; flex-shrink: 0; }
          .ca-heading { font-size: 20px; line-height: 26px; }
        }

        @media (max-width: 480px) {
          .ca-wrapper { padding: 12px 52px 32px 12px; }
          .ca-face-wrapper { width: 180px; height: 212px; }
          .ca-stage-row { gap: 8px; }
          .ca-stage-chip { padding: 6px 10px; font-size: 11px; }
          .ca-btn-primary { width: 100%; justify-content: center; }
        }
      `}</style>

      <div className="ca-wrapper">
        <div className="ca-header">
          <button className="ca-back" onClick={handleBack}>
            <ChevronLeft size={20} />
            Analysis
          </button>

          <div className="ca-stepper">
            <span className="ca-stepper-caption">Step 3 of 4</span>
            <div className="ca-stepper-track">
              {STEPPER.map((label, i) => {
                const done = i < 2 || (i === 2 && complete);
                const active = i === 2 && !complete;
                return (
                  <React.Fragment key={label}>
                    {i > 0 && <span className={`ca-stepper-line${i <= (complete ? 3 : 2) ? ' done' : ''}`} />}
                    <div className={`ca-stepper-item${active ? ' active' : ''}${done ? ' done' : ''}`}>
                      <span className="ca-stepper-circle">{done ? <Check size={13} /> : i + 1}</span>
                      <span className="ca-stepper-label">{label}</span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <span />
        </div>

        <div className="ca-center">
          <div className="ca-face-wrapper">
            <div className="ca-face-inner">
              {frontImage ? (
                <img src={frontImage} alt="Your scan" className="ca-face-photo" />
              ) : (
                <div className="ca-face-placeholder">
                  <ScanFace size={72} />
                </div>
              )}

              {!complete && <span className="ca-scan-line" />}

              {complete && (
                <svg viewBox="0 0 220 260" className="ca-checkmark-svg">
                  <defs>
                    <linearGradient id="caCheckGrad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#004276" />
                      <stop offset="100%" stopColor="#004276" />
                    </linearGradient>
                  </defs>
                  <polyline
                    points="70,150 100,185 165,105"
                    fill="none"
                    stroke="url(#caCheckGrad)"
                    strokeWidth="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          </div>

          <h1 className="ca-heading">
            {complete ? 'Your personalized skin analysis is ready' : 'Refining your skin analysis with your details...'}
          </h1>
          <p className="ca-subtext">
            {complete
              ? "We've combined your skin scan and details to generate accurate scores, insights, and recommendations tailored just for you."
              : "Your skin scan is complete - now we're combining it with your inputs to generate accurate scores and personalized recommendations."}
          </p>

          <div className="ca-stage-row">
            {STAGES.map(({ key, icon, label }, i) => {
              const done = i < activeStageIndex || complete;
              const active = i === activeStageIndex && !complete;
              return (
                <span className={`ca-stage-chip${active ? ' active' : ''}${done ? ' done' : ''}`} key={key}>
                  <img src={icon} alt="" className="ca-stage-icon" />
                  {label}
                  {done && (
                    <span className="ca-stage-check">
                      <Check size={10} />
                    </span>
                  )}
                </span>
              );
            })}
          </div>

          <div className="ca-progress-row">
            <span className="ca-progress-label">{complete ? 'Completed' : 'Processing'}</span>
            <span className="ca-progress-pct">{progress} %</span>
          </div>
          <div className="ca-progress-track">
            <div className="ca-progress-fill" style={{ width: `${progress}%` }} />
            <span className="ca-progress-handle" style={{ left: `${progress}%` }} />
          </div>

          <p className="ca-caption">
            {complete
              ? 'Skin Score and Insights are ready !'
              : timedOut
                ? 'Still working on it — this can take several minutes. Feel free to check back later, or check again now.'
                : 'This can take a few minutes — feel free to keep this tab open in the background.'}
          </p>

          {pollError && <p className="ca-caption" style={{ color: '#D14343', marginTop: '-16px' }}>{pollError}</p>}

          {complete && resultRecord?.FAILEDREASON && (
            <p className="ca-caption" style={{ color: '#B8860B', marginTop: '-16px' }}>
              Note: {resultRecord.FAILEDREASON}
            </p>
          )}

          {complete && (
            <button className="ca-btn-primary pulsing" onClick={handleViewResults}>
              View My Results
            </button>
          )}

          {!complete && timedOut && (
            <button className="ca-btn-primary" onClick={handleCheckAgain} disabled={checkingNow}>
              {checkingNow ? 'Checking…' : 'Check Again'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CosmeticAnalysis;
